import type { Dictionary } from '@/i18n/dictionary'
import { mediusTermsEn } from '@/i18n/medius-terms'
import { mediusDeletionEn, mediusPrivacyEn } from '@/i18n/medius-policies'
import { sparsaAcceptableUseEn, sparsaTermsEn } from '@/i18n/sparsa-terms'
import { sparsaDeletionEn, sparsaPrivacyEn } from '@/i18n/sparsa-policies'

export const it: Dictionary = {
  meta: {
    home: {
      title: 'Dalmos Systems — Software aziendale su misura, CRM e integrazioni',
      description:
        'Dalmos Systems sviluppa software aziendale su misura per finanza, immobiliare, hospitality e logistica — integrazioni CRM, ERP e PMS, infrastruttura cloud e manutenzione a lungo termine.',
      keywords: [
        'software aziendale su misura',
        'implementazione CRM',
        'software immobiliare',
        'software per hotel e hospitality',
        'integrazioni ERP e PMS',
        'integrazioni API',
        'Microsoft Dynamics 365',
        'Power Platform',
        'sviluppo software Croazia',
      ],
    },
    privacy: {
      title: 'Informativa sulla privacy — Dalmos Systems',
      description:
        'Come Dalmos Systems tratta i dati personali raccolti tramite il modulo di contatto, nel rispetto del GDPR.',
    },
    legal: {
      title: 'Note legali — Dalmos Systems',
      description:
        'Dati legali di Dalmos Systems: denominazione registrata, sede a Šibenik, iscrizione al registro croato dell’artigianato e contatti.',
    },
    mediusPrivacy: {
      title: 'Medius – informativa privacy (inglese) — Dalmos Systems',
      description:
        'Come Medius di Dalmos Systems tratta i dati personali per conto dei suoi clienti. L’informativa è disponibile in inglese e in croato.',
    },
    mediusDeletion: {
      title: 'Medius – cancellazione dei dati (inglese) — Dalmos Systems',
      description:
        'Come richiedere la cancellazione dei dati personali da Medius, compresi i messaggi ricevuti tramite Facebook Messenger, Instagram e WhatsApp.',
    },
    mediusTerms: {
      title: 'Medius – termini di servizio (inglese) — Dalmos Systems',
      description:
        'Le condizioni alle quali Dalmos Systems fornisce Medius ai clienti aziendali. I termini sono disponibili in inglese e in croato.',
    },
    sparsaPrivacy: {
      title: 'Sparsa – informativa privacy (inglese) — Dalmos Systems',
      description:
        'Come Sparsa di Dalmos Systems tratta i dati personali dei destinatari delle newsletter. L’informativa è disponibile in inglese e in croato.',
    },
    sparsaDeletion: {
      title: 'Sparsa – cancellazione dei dati (inglese) — Dalmos Systems',
      description:
        'Come disiscriversi dalle newsletter inviate tramite Sparsa e come richiedere la cancellazione dei propri dati personali.',
    },
    sparsaTerms: {
      title: 'Sparsa – termini di servizio (inglese) — Dalmos Systems',
      description:
        'Le condizioni alle quali Dalmos Systems fornisce Sparsa, il suo servizio di newsletter, alle aziende. I termini sono disponibili in inglese e in croato.',
    },
    sparsaAcceptableUse: {
      title: 'Sparsa – uso accettabile (inglese) — Dalmos Systems',
      description:
        'Le regole di invio con Sparsa: consenso di ogni destinatario, nessuna lista acquistata, dichiarazione per ogni importazione e segnalazione degli abusi.',
    },
    notFound: {
      title: 'Pagina non trovata — Dalmos Systems',
      description: 'Non abbiamo trovato la pagina richiesta.',
    },
  },
  nav: {
    home: 'Home',
    services: 'Servizi',
    about: 'Chi siamo',
    contact: 'Contatti',
    cta: 'Richiedi un preventivo',
    openMenu: 'Apri il menu',
    closeMenu: 'Chiudi il menu',
    languageLabel: 'Scegli la lingua',
    currentLanguage: 'Lingua corrente',
  },
  a11y: {
    skipToContent: 'Vai al contenuto',
    decorative: 'Elemento decorativo',
  },
  hero: {
    eyebrow: 'Soluzioni software su misura per le imprese',
    headlineBefore: 'Sistemi che lavorano per la tua azienda,',
    headlineAccent: 'non il contrario.',
    subtitle:
      'Un partner tecnico per il software aziendale su misura — soluzioni per finanza, immobiliare, hospitality e logistica, costruite sull’esperienza di grandi progetti CRM, con l’ecosistema Microsoft come base affidabile e non come unico riferimento.',
    ctaPrimary: 'Richiedi un preventivo',
    ctaSecondary: 'Scopri i servizi',
    chips: [
      'Integrazioni CRM ed ERP',
      'Hospitality e PMS',
      'Soluzioni finanziarie',
      'Immobiliare',
      'App mobili',
      'Magazzino e facility',
    ],
    scrollHint: 'Scorri',
  },
  stats: {
    title: 'Su cosa costruiamo',
    items: [
      { value: '20+', label: 'Aree di competenza sotto un unico tetto' },
      { value: '6', label: 'Settori con modelli di soluzione collaudati' },
      { value: '1', label: 'Persona che guida il progetto e scrive il codice' },
      { value: '100%', label: 'Dei progetti consegnati con supporto definito' },
    ],
  },
  why: {
    title: 'Perché Dalmos Systems',
    cards: [
      {
        title: 'Accesso diretto',
        body: 'Nessuna agenzia di mezzo — parli direttamente con la persona che realizza il progetto.',
      },
      {
        title: 'Soluzioni modellate sul settore',
        body: 'Dalla finanza e dal settore immobiliare all’hospitality e alla logistica — ogni sistema nasce intorno ai tuoi processi reali.',
      },
      {
        title: 'Un partner, non un fornitore',
        body: 'Continuiamo a seguire il sistema dopo la consegna — manutenzione, aggiornamenti e supporto fanno parte dell’accordo.',
      },
    ],
  },
  services: {
    title: 'Un ampio ventaglio di soluzioni, un unico interlocutore',
    lede: 'Dalla finanza e dal settore immobiliare all’hospitality e all’infrastruttura cloud — ogni servizio include implementazione e manutenzione a lungo termine, senza concentrarsi troppo su una singola nicchia.',
    items: {
      crm: {
        title: 'CRM e personalizzazioni',
        body: 'Introduzione e adattamento dei sistemi CRM ai tuoi processi, team e clienti.',
      },
      erp: {
        title: 'Sistemi ERP e integrazioni',
        body: 'Collegamento dell’ERP con vendite, magazzino e finanza e adattamento dei moduli esistenti.',
      },
      processes: {
        title: 'Automazione e ottimizzazione dei processi',
        body: 'Automazione dei flussi di lavoro interni e applicazioni che accelerano le decisioni e riducono il lavoro manuale.',
      },
      finance: {
        title: 'Soluzioni finanziarie',
        body: 'Sistemi per il controllo dei costi, la fatturazione e la reportistica finanziaria.',
      },
      realEstate: {
        title: 'Immobiliare e asset management',
        body: 'Gestione di portafogli immobiliari, contratti di locazione e valutazioni.',
      },
      mobile: {
        title: 'Applicazioni mobili',
        body: 'Estensioni mobili dei sistemi aziendali per i team sul campo, in magazzino o nelle vendite.',
      },
      hospitality: {
        title: 'Soluzioni per l’hospitality',
        body: 'Sistemi per hotel, ristorazione e strutture ricettive — integrazioni PMS, prenotazioni, dati degli ospiti e operatività.',
      },
      web: {
        title: 'Soluzioni web e portali',
        body: 'Siti web allineati al tuo sistema aziendale.',
      },
      reservations: {
        title: 'Sistemi di prenotazione',
        body: 'Prenotazioni online in tempo reale, collegate al PMS e alla fatturazione.',
      },
      analytics: {
        title: 'Statistiche e report',
        body: 'Dashboard per decisioni basate su dati reali.',
      },
      loyalty: {
        title: 'Programmi fedeltà',
        body: 'Programmi fedeltà con punti, livelli e premi, tessere fedeltà e un’app mobile o una carta nel wallet del telefono. Offerte e campagne si basano sullo storico degli acquisti di ogni cliente, e tutto resta collegato al tuo CRM.',
      },
      marketing: {
        title: 'Marketing e newsletter',
        body: 'Newsletter e campagne verso i contatti del tuo CRM, con consensi e disiscrizioni integrati.',
      },
      communication: {
        title: 'Soluzioni di comunicazione',
        body: 'Soluzioni su misura per il modo in cui il tuo team comunica con i clienti, integrate nel tuo CRM o autonome.',
      },
      warehouse: {
        title: 'Gestione del magazzino',
        body: 'Monitoraggio delle giacenze e dei movimenti merci.',
      },
      facility: {
        title: 'Gestione delle strutture',
        body: 'Gestione degli immobili e della manutenzione.',
      },
      api: {
        title: 'Integrazioni API e middleware',
        body: 'Collegamento di CRM, ERP, PMS e altri sistemi che non comunicano tra loro.',
      },
      cloud: {
        title: 'Infrastruttura cloud e DevOps',
        body: 'Ambienti Azure, CI/CD e automazione.',
      },
      microsoft: {
        title: 'Ecosistema Microsoft',
        body: 'Dynamics 365, Power Platform e Azure, quando sono la scelta giusta per il business.',
      },
      security: {
        title: 'Sicurezza IT e conformità',
        body: 'Protezione dei dati e conformità al GDPR.',
      },
      custom: {
        title: 'Soluzioni software personalizzate',
        body: 'Soluzioni su misura per esigenze specifiche.',
      },
      training: {
        title: 'Formazione e onboarding',
        body: 'Formazione degli utenti e documentazione interna.',
      },
      support: {
        title: 'Supporto e contratti SLA',
        body: 'Tempi di risposta e di supporto definiti.',
      },
    },
  },
  integrations: {
    title: 'Sistemi che finalmente comunicano',
    lede: 'CRM, ERP e PMS raramente si collegano da soli. Li integriamo in modo che i dati vengano inseriti una volta e siano visibili ovunque — senza reinserimenti manuali né fogli di calcolo intermedi.',
    hubLabel: 'Un’unica fonte di verità',
    systems: [
      {
        code: 'CRM',
        title: 'Clienti e vendite',
        body: 'Clienti, opportunità e comunicazioni in un unico posto, sincronizzati con offerte, contratti e fatturazione.',
      },
      {
        code: 'ERP',
        title: 'Finanza e operatività',
        body: 'Fatturazione, acquisti e scorte collegati a vendite e magazzino, senza doppio inserimento.',
      },
      {
        code: 'PMS',
        title: 'Hospitality',
        body: 'Sistemi di property management collegati a prenotazioni, CRM e contabilità.',
      },
      {
        code: 'API',
        title: 'Tutto il resto',
        body: 'Contabilità, e-commerce, POS, channel manager, strumenti BI e applicazioni interne.',
      },
    ],
    capabilitiesTitle: 'Cosa ottieni',
    capabilities: [
      'Sincronizzazione bidirezionale dei dati in tempo reale',
      'Migrazioni dei dati senza fermi operativi',
      'Middleware e API su misura',
      'Monitoraggio, log e avvisi quando qualcosa si blocca',
    ],
  },
  about: {
    title: 'Chi siamo',
    statementBefore: 'Azienda nuova.',
    statementAccent: 'Esperienza che non parte da zero.',
    lede: 'Dalmos Systems nasce come partner tecnico per il software aziendale su misura — finanza, immobiliare, hospitality, logistica e oltre. L’approccio è la continuazione diretta del lavoro su grandi progetti di migrazione e integrazione CRM nei settori immobiliare e alberghiero, senza una grande organizzazione in mezzo.',
    processTitle: 'Come lavoriamo',
    steps: [
      {
        index: '01',
        title: 'Analisi',
        body: 'Capire i processi prima della prima riga di codice.',
      },
      {
        index: '02',
        title: 'Progettazione',
        body: 'Architettura adattata ai tuoi sistemi, non il contrario.',
      },
      {
        index: '03',
        title: 'Implementazione',
        body: 'Consegna iterativa con verifiche regolari insieme a te.',
      },
      { index: '04', title: 'Supporto', body: 'Seguiamo il sistema anche dopo il lancio.' },
    ],
    valuesTitle: 'Cosa ci guida',
    values: [
      {
        title: 'Accesso diretto',
        body: 'La persona che guida il progetto è quella con cui parli, senza intermediari.',
      },
      {
        title: 'Trasparenza',
        body: 'Perimetro chiaro, prezzo chiaro e confini del progetto chiari prima della prima riga di codice.',
      },
      {
        title: 'Lungo termine',
        body: 'Costruiamo sistemi di cui curiamo noi stessi la manutenzione, non progetti che lasciamo alle spalle.',
      },
    ],
  },
  contact: {
    title: 'Parliamo del tuo sistema',
    lede: 'Scrivici con una descrizione del progetto — rispondiamo con una proposta per il passo successivo.',
    responseNote: 'Rispondiamo entro {days} giorni lavorativi.',
    emailLabel: 'E-mail',
    phoneLabel: 'Telefono',
    form: {
      name: 'Nome e cognome',
      namePlaceholder: 'Anna Rossi',
      email: 'E-mail',
      emailPlaceholder: 'anna@azienda.it',
      company: 'Azienda',
      companyPlaceholder: 'Nome azienda (facoltativo)',
      message: 'Messaggio',
      messagePlaceholder: 'Descrivi il progetto, i sistemi che usi e i tempi desiderati.',
      consent: 'Acconsento all’uso dei miei dati per rispondere a questa richiesta.',
      submit: 'Invia richiesta',
      submitting: 'Invio…',
      successTitle: 'Richiesta inviata.',
      successBody: 'Ti risponderemo all’indirizzo e-mail indicato.',
      errorTitle: 'Invio non riuscito.',
      errorBody: 'Riprova oppure scrivici direttamente via e-mail.',
      fallbackHint: 'Apri il client e-mail',
      errors: {
        nameRequired: 'Inserisci nome e cognome.',
        emailRequired: 'Inserisci il tuo indirizzo e-mail.',
        emailInvalid: 'Questo indirizzo e-mail non è valido.',
        messageRequired: 'Inserisci un messaggio.',
        messageTooShort: 'Il messaggio deve contenere almeno 20 caratteri.',
        consentRequired: 'È necessario il consenso al trattamento dei dati.',
      },
    },
  },
  footer: {
    tagline: 'Software aziendale su misura per finanza, immobiliare, hospitality e logistica.',
    navTitle: 'Navigazione',
    legalTitle: 'Informazioni legali',
    privacy: 'Informativa sulla privacy',
    legalNotice: 'Note legali',
    rights: 'Tutti i diritti riservati.',
    builtIn: 'Ulica Sedmog kontinenta 1, 22000 Šibenik, Croazia',
  },
  privacy: {
    title: 'Informativa sulla privacy',
    lastUpdatedLabel: 'Ultimo aggiornamento',
    lastUpdated: '2026-10-31',
    intro:
      'Questa informativa descrive come Dalmos Systems tratta i dati personali che ci invii attraverso questo sito web.',
    sections: [
      {
        heading: 'Titolare del trattamento',
        paragraphs: [
          'Il titolare del trattamento è {controller}. Per qualsiasi domanda sul trattamento dei dati scrivi a {email}.',
        ],
      },
      {
        heading: 'Quali dati raccogliamo',
        paragraphs: [
          'Tramite il modulo di contatto raccogliamo nome e cognome, indirizzo e-mail, nome dell’azienda se indicato e il contenuto del messaggio.',
          'Non utilizziamo cookie pubblicitari né strumenti che tracciano i visitatori su altri siti web.',
          'Contiamo le visite con Cloudflare Web Analytics, uno script di Cloudflare senza cookie (static.cloudflareinsights.com). Quando una pagina si carica, lo script invia a Cloudflare dati anonimi sulle visualizzazioni e sulle prestazioni: l’indirizzo della pagina, la pagina da cui provieni, i tempi di caricamento e misurazioni simili delle prestazioni della pagina; Cloudflare aggiunge dalla richiesta il browser, il sistema operativo, il tipo di dispositivo e il paese. Lo script non imposta cookie, non usa altra memoria sul tuo dispositivo e non crea alcun identificativo del visitatore. Il tuo indirizzo IP raggiunge Cloudflare con la richiesta, come per qualsiasi sito; Cloudflare non lo usa per identificarti e non lo conserva a tal fine. Vediamo solo dati aggregati, come visualizzazioni di pagina, siti di provenienza, paesi e tipi di dispositivo, e li usiamo per capire come viene usato il sito e mantenerlo veloce. La base giuridica è il nostro legittimo interesse a gestire e migliorare il sito (articolo 6, paragrafo 1, lettera f), del GDPR). Cloudflare tratta i dati per nostro conto in qualità di responsabile del trattamento, in base al proprio accordo sul trattamento dei dati con le clausole contrattuali tipo dell’UE per i trasferimenti fuori dall’UE. Conserva i dati completi per 7 giorni e poi un campione di circa un decimo, dal quale vediamo le statistiche degli ultimi sei mesi. Puoi bloccare lo script dalle impostazioni del browser o con un blocco dei contenuti; il sito funziona allo stesso modo anche senza. Lo stesso vale per il sito di Medius, medius.dalmossystems.com, che rimanda a questa informativa.',
        ],
      },
      {
        heading: 'Finalità e base giuridica',
        paragraphs: [
          'Utilizziamo i dati esclusivamente per rispondere alla tua richiesta e preparare un preventivo. La base giuridica è il tuo consenso insieme al nostro legittimo interesse a comunicare con potenziali clienti.',
        ],
      },
      {
        heading: 'Periodo di conservazione',
        paragraphs: [
          'Conserviamo le richieste per un massimo di ventiquattro mesi dall’ultima comunicazione, dopo i quali vengono cancellate, salvo che una conservazione più lunga sia richiesta da obblighi contrattuali o di legge.',
        ],
      },
      {
        heading: 'I tuoi diritti',
        paragraphs: [
          'Hai diritto di accedere ai tuoi dati, di ottenerne la rettifica, la cancellazione, la limitazione del trattamento e la portabilità, e di revocare il consenso in qualsiasi momento.',
          'Puoi presentare reclamo all’Agenzia croata per la protezione dei dati personali (AZOP), in qualità di autorità di controllo.',
        ],
      },
      {
        heading: 'Sicurezza e trasferimento dei dati',
        paragraphs: [
          'Il traffico tra il tuo browser e i nostri server è cifrato. Non vendiamo i tuoi dati e non li condividiamo con terze parti, a eccezione dei fornitori che ci assistono nella gestione del sito e nella comunicazione, sulla base di un accordo di trattamento dei dati.',
        ],
      },
    ],
    backHome: 'Torna alla home',
  },
  legal: {
    title: 'Note legali',
    intro:
      'Informazioni sul fornitore del servizio ai sensi della legge croata sul commercio elettronico.',
    nameLabel: 'Denominazione registrata',
    shortNameLabel: 'Denominazione breve',
    seatLabel: 'Sede',
    country: 'Croazia',
    registerLabel: 'Registro',
    registerValue: 'Registro croato dell’artigianato (Obrtni registar), Contea di Šibenik-Knin',
    mboLabel: 'Numero di registrazione (MBO)',
    registeredOnLabel: 'Data di iscrizione',
    activityLabel: 'Attività principale',
    activityValue:
      'Realizzazione, sviluppo e manutenzione di programmi informatici e applicazioni su commissione (NKD 2025: 62.10.9)',
    contactLabel: 'Contatti',
    backHome: 'Torna alla home',
  },
  mediusPrivacy: {
    ...mediusPrivacyEn,
    lastUpdatedLabel: 'In vigore dal',
    languageNote:
      'Questa informativa di Medius è disponibile in inglese e in croato; di seguito la versione inglese.',
    backHome: 'Torna alla home',
  },
  mediusDeletion: {
    ...mediusDeletionEn,
    lastUpdatedLabel: 'In vigore dal',
    languageNote:
      'Queste istruzioni sono disponibili in inglese e in croato; di seguito la versione inglese.',
    backHome: 'Torna alla home',
  },
  mediusTerms: {
    ...mediusTermsEn,
    lastUpdatedLabel: 'In vigore dal',
    languageNote:
      'Questi termini di Medius sono disponibili in inglese e in croato; di seguito la versione inglese.',
    backHome: 'Torna alla home',
  },
  mediusLinks: {
    mediusPrivacy: 'Medius – informativa privacy',
    mediusDeletion: 'Richiedere la cancellazione dei dati',
    mediusTerms: 'Medius – termini di servizio',
  },
  sparsaPrivacy: {
    ...sparsaPrivacyEn,
    lastUpdatedLabel: 'In vigore dal',
    languageNote:
      'Questa informativa di Sparsa è disponibile in inglese e in croato; di seguito la versione inglese.',
    backHome: 'Torna alla home',
  },
  sparsaDeletion: {
    ...sparsaDeletionEn,
    lastUpdatedLabel: 'In vigore dal',
    languageNote:
      'Queste istruzioni sono disponibili in inglese e in croato; di seguito la versione inglese.',
    backHome: 'Torna alla home',
  },
  sparsaTerms: {
    ...sparsaTermsEn,
    lastUpdatedLabel: 'In vigore dal',
    languageNote:
      'Questi termini di Sparsa sono disponibili in inglese e in croato; di seguito la versione inglese.',
    backHome: 'Torna alla home',
  },
  sparsaAcceptableUse: {
    ...sparsaAcceptableUseEn,
    lastUpdatedLabel: 'In vigore dal',
    languageNote:
      'Questa policy di uso accettabile di Sparsa è disponibile in inglese e in croato; di seguito la versione inglese.',
    backHome: 'Torna alla home',
  },
  sparsaLinks: {
    sparsaPrivacy: 'Sparsa – informativa privacy',
    sparsaDeletion: 'Disiscriversi e cancellare i dati',
    sparsaTerms: 'Sparsa – termini di servizio',
    sparsaAcceptableUse: 'Sparsa – uso accettabile',
  },
  notFound: {
    code: '404',
    title: 'Pagina non trovata',
    body: 'L’indirizzo che hai aperto non esiste o è stato spostato.',
    cta: 'Torna alla home',
  },
}
